// /root/projects/src/pages/admin/UserManagementPage.jsx

// ۱. همه‌ی importها در بالا:
import React, { useEffect, useState, useCallback } from 'react';
import * as jwt_decode from 'jwt-decode';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import EditUserModal from '../../components/modals/EditUserModal';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';
import AnalyticsOverview from '../../components/analytics/AnalyticsOverview';

function isTokenExpired(token) {
  try {
    const { exp } = jwt_decode(token);
    return Date.now() >= exp * 1000;
  } catch {
    return true;
  }
}

const UserManagementPage = () => {
  const { token, user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchUsers = useCallback(async () => {
    if (!token) {
      setError('Authentication token not found. Please log in.');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch('/api/users', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include'
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to fetch users: ${response.status}`);
      }
      const data = await response.json();
      if (data && data.status === 'success' && data.data && Array.isArray(data.data.users)) {
        setUsers(data.data.users);
      } else {
        console.error('Unexpected data structure for users:', data);
        setUsers([]);
        if (data && data.status === 'success') {
          setError('Failed to parse user data from server.');
        } else if (!data?.status) {
          setError('Received malformed success response from server.');
        }
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      setError(err.message || 'An error occurred while fetching users.');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!authLoading) {
      if (!user || user.role !== 'admin') {
        navigate('/');
      } else {
        fetchUsers();
      }
    }
  }, [user, authLoading, navigate, fetchUsers]);

  const handleOpenEditModal = (userToEdit) => {
    setEditingUser(userToEdit);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditingUser(null);
    setIsEditModalOpen(false);
  };

  const handleSaveUser = async (userId, updatedUserData) => {
    if (!token) {
      setError('Authentication token not found. Please log in.');
      return;
    }
    setIsSaving(true);
    setError('');
    try {
      const response = await fetch(`/api/users/admin/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(updatedUserData),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to update user: ${response.status}`);
      }

      await response.json();

      setUsers(prevUsers => 
        prevUsers.map(u => (u.id === userId ? { ...u, ...updatedUserData } : u))
      );
      handleCloseEditModal();
    } catch (err) {
      console.error("Error updating user:", err);
      alert(`Error: ${err.message || 'Could not update user.'}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Delete user handlers
  const handleOpenDeleteModal = (userToDelete) => {
    if (userToDelete.id === user.id) {
      alert('You cannot delete your own account.');
      return;
    }
    setUserToDelete(userToDelete);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setUserToDelete(null);
    setIsDeleteModalOpen(false);
  };

  const handleDeleteUser = async () => {
    if (!token || !userToDelete) {
      setError('Authentication token not found. Please log in.');
      return;
    }
    
    setIsDeleting(true);
    setError('');
    try {
      const response = await fetch(`/api/users/${userToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to delete user: ${response.status}`);
      }

      const result = await response.json();
      console.log('User deleted successfully:', result);

      // Remove user from the local state
      setUsers(prevUsers => prevUsers.filter(u => u.id !== userToDelete.id));
      
      handleCloseDeleteModal();
      
      // Show success message
      alert(`User "${userToDelete.username}" has been deleted successfully.`);
      
    } catch (err) {
      console.error("Error deleting user:", err);
      alert(`Error: ${err.message || 'Could not delete user.'}`);
    } finally {
      setIsDeleting(false);
    }
  };

  if (authLoading || isLoading) {
    return (
      <div className="container mx-auto p-4 md:p-8">
        <h1 className="text-2xl font-semibold text-brand-text-dark mb-6">User Management</h1>
        <p>Loading users...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-8">
      <h1 className="text-2xl font-semibold text-brand-text-dark mb-6">User Management</h1>
      
      {/* Website Analytics Section */}
      <div className="mb-8">
        <AnalyticsOverview days={30} />
      </div>

      {/* Quick Navigation Section */}
      <div className="mb-8 p-6 bg-white shadow-md rounded-lg">
        <h2 className="text-xl font-semibold text-brand-text-dark mb-4">Admin Modules</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {/* Service Management */}
          <Link to="/admin/service-requests-management" className="bg-teal-500 hover:bg-teal-600 text-white font-semibold py-3 px-4 rounded-lg text-center transition duration-150 ease-in-out">
            <div className="flex flex-col items-center">
              <svg className="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
              Manage Service Requests
            </div>
          </Link>
          
          {/* Accounting Modules */}
          <Link to="/admin/estimates" className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-4 rounded-lg text-center transition duration-150 ease-in-out">
            Estimates
          </Link>
          <Link to="/admin/quotes" className="bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-4 rounded-lg text-center transition duration-150 ease-in-out">
            Quotes
          </Link>
          <Link to="/admin/invoices" className="bg-indigo-500 hover:bg-indigo-600 text-white font-semibold py-3 px-4 rounded-lg text-center transition duration-150 ease-in-out">
            Invoices
          </Link>
          <Link to="/admin/expenses" className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-4 rounded-lg text-center transition duration-150 ease-in-out">
            Expenses
          </Link>
          <Link to="/admin/tax-reporting" className="bg-purple-500 hover:bg-purple-600 text-white font-semibold py-3 px-4 rounded-lg text-center transition duration-150 ease-in-out">
            Tax Reporting
          </Link>
          <Link to="/admin/inventory" className="bg-purple-500 hover:bg-purple-600 text-white font-semibold py-3 px-4 rounded-lg text-center transition duration-150 ease-in-out">
            <div className="flex flex-col items-center">
              <svg className="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              Inventory Management
            </div>
          </Link>
          <Link to="/admin/customers" className="bg-pink-500 hover:bg-pink-600 text-white font-semibold py-3 px-4 rounded-lg text-center transition duration-150 ease-in-out">
            <div className="flex flex-col items-center">
              <svg className="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Customers
            </div>
          </Link>
          <Link to="/pinvoices" className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold py-3 px-4 rounded-lg text-center transition duration-150 ease-in-out">
            <div className="flex flex-col items-center">
              <svg className="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Public Invoice
            </div>
          </Link>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      {users.length === 0 && !isLoading && !error && (
        <p>No users found.</p>
      )}

      {users.length > 0 && (
        <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((u) => (
                <tr key={u.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{u.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{u.username}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{u.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{u.phone || 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      u.role === 'admin' ? 'bg-red-100 text-red-800' :
                      u.role === 'manager' ? 'bg-yellow-100 text-yellow-800' :
                      u.role === 'staff' ? 'bg-blue-100 text-blue-800' :
                      'bg-green-100 text-green-800' // client
                    }`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleOpenEditModal(u)}
                      className="text-brand-primary hover:text-brand-primary-dark"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleOpenDeleteModal(u)}
                      className="text-red-600 hover:text-red-900"
                      disabled={u.id === user.id}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isEditModalOpen && editingUser && (
        <EditUserModal
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          user={editingUser}
          onSave={handleSaveUser}
          isLoading={isSaving}
        />
      )}

      {isDeleteModalOpen && userToDelete && (
        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={handleCloseDeleteModal}
          onConfirm={handleDeleteUser}
          isLoading={isDeleting}
          title="Delete User"
          message={`Are you sure you want to delete the user "${userToDelete.username}"? This action cannot be undone.`}
        />
      )}
    </div>
  );
};

export default UserManagementPage;
